import type React from 'react';
import {useMemo} from 'react';

import {
  apiCreateOrUpdateMultipleFdApplicationLivestock,
  apiDeleteFdApplicationLivestock,
} from 'constants/Api';

import type {
  IApplicationLivestockRecord,
  ILivestockRecordSource,
  ILivestockFormState,
  IUseLivestockSubStepParams,
  IUseLivestockSubStepResult,
  SellOffFrequency,
} from '../productionInfo.types';
import {
  EMPTY_LIVESTOCK_FORM,
  lshFormatEnumLabel,
  lshFormatLivestockLabel,
  lshGenerateSellOffMonths,
  lshNormalizeProductGrading,
  lshToNumberOrZero,
} from '../productionInfo.utils';

export function lshUseLivestockSubStep({
  applicationId,
  isSavingLivestock,
  livestockCatalogue,
  livestockRecords,
  livestockForm,
  sellOffFrequency,
  sellOffMonth,
  customSellOffValues,
  setLivestockError,
  setIsSavingLivestock,
  setLivestockForm,
  setSellOffFrequency,
  setSellOffMonth,
  setCustomSellOffValues,
  setLivestockRecords,
  mapLivestockRecord,
}: IUseLivestockSubStepParams): IUseLivestockSubStepResult {
  const onceOffMonths = useMemo(() => lshGenerateSellOffMonths(12), []);
  const customMonths = useMemo(() => lshGenerateSellOffMonths(18), []);

  const livestockCatalogueById = useMemo(() => {
    const catalogueMap = new Map<
      number,
      IApplicationLivestockRecord['livestock']
    >();

    livestockCatalogue.forEach((item) => {
      catalogueMap.set(Number(item.id), item);
    });

    return catalogueMap;
  }, [livestockCatalogue]);

  const livestockSelectOptions = useMemo(() => {
    const uniqueByName = new Map<string, (typeof livestockCatalogue)[number]>();

    livestockCatalogue.forEach((item) => {
      const nameKey = String(item?.name || '')
        .trim()
        .toUpperCase();
      if (!nameKey) return;

      if (!uniqueByName.has(nameKey)) {
        uniqueByName.set(nameKey, item);
      }
    });

    return Array.from(uniqueByName.values())
      .sort((left, right) =>
        lshFormatLivestockLabel(left.name).localeCompare(
          lshFormatLivestockLabel(right.name),
        ),
      )
      .map((item) => ({
        value: String(item.id),
        label: lshFormatLivestockLabel(item.name),
      }));
  }, [livestockCatalogue]);

  const livestockTypeOptions = useMemo(() => {
    const uniqueTypes = new Set<string>();

    livestockCatalogue.forEach((item) => {
      const value = String(item?.livestockType || '')
        .trim()
        .toUpperCase();
      if (value) uniqueTypes.add(value);
    });

    return Array.from(uniqueTypes)
      .sort((left, right) =>
        lshFormatEnumLabel(left).localeCompare(lshFormatEnumLabel(right)),
      )
      .map((value) => ({
        value,
        label: lshFormatEnumLabel(value),
      }));
  }, [livestockCatalogue]);

  const handleLivestockFormChange =
    (field: keyof ILivestockFormState) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setLivestockError(null);
      const rawValue = event.target.value;
      const value =
        field === 'productGrading' ? rawValue.toUpperCase() : rawValue;

      setLivestockForm((current) => ({
        ...current,
        [field]: value,
      }));

      if (field === 'livestockId') {
        const selected = livestockCatalogueById.get(Number(rawValue));
        if (!selected) return;

        setLivestockForm((current) => ({
          ...current,
          livestockId: rawValue,
          type: String(selected.livestockType || current.type || '').trim(),
        }));
      }
    };

  const handleSellOffFrequencyChange = (value: SellOffFrequency) => {
    setSellOffFrequency(value);
    if (value !== 'once-off') {
      setSellOffMonth('');
    }
    if (value !== 'custom') {
      setCustomSellOffValues({});
    }
  };

  const handleSellOffMonthChange = (value: string) => {
    setSellOffMonth(value);
  };

  const handleCustomSellOffValueChange = (monthKey: string, value: string) => {
    setCustomSellOffValues((current) => {
      if (!value.trim()) {
        const next = {...current};
        delete next[monthKey];
        return next;
      }

      return {
        ...current,
        [monthKey]: value,
      };
    });
  };

  const handleAddLivestockRecord = () => {
    setLivestockError(null);

    if (
      !livestockForm.livestockId ||
      !livestockForm.quantity.trim() ||
      !livestockForm.productGrading.trim()
    ) {
      setLivestockError(
        'Select livestock and provide quantity + grading before adding.',
      );
      return;
    }

    const productGrading = lshNormalizeProductGrading(
      livestockForm.productGrading,
    );
    if (!productGrading) {
      setLivestockError('Product grading must be A, B or C.');
      return;
    }

    const selectedLivestock = livestockCatalogueById.get(
      Number(livestockForm.livestockId),
    );
    if (!selectedLivestock) {
      setLivestockError('Invalid livestock selection.');
      return;
    }

    if (sellOffFrequency === 'once-off' && !sellOffMonth) {
      setLivestockError(
        'Select a once-off sell month before adding this record.',
      );
      return;
    }

    if (sellOffFrequency === 'custom') {
      const hasAnyMonthlyValue = Object.values(customSellOffValues).some(
        (value) => lshToNumberOrZero(value) > 0,
      );
      if (!hasAnyMonthlyValue) {
        setLivestockError(
          'Add at least one monthly sell-off quantity for custom frequency.',
        );
        return;
      }
    }

    const serialisedSellOffPeriod =
      sellOffFrequency === 'once-off'
        ? JSON.stringify({
            frequency: 'once-off',
            month: sellOffMonth,
            monthlyValues: {},
          })
        : sellOffFrequency === 'custom'
        ? JSON.stringify({
            frequency: 'custom',
            monthlyValues: Object.entries(customSellOffValues).reduce<
              Record<string, number>
            >((acc, [month, amount]) => {
              const parsed = lshToNumberOrZero(amount);
              if (parsed > 0) {
                acc[month] = parsed;
              }
              return acc;
            }, {}),
          })
        : JSON.stringify({
            frequency: 'not-selling',
            monthlyValues: {},
          });

    const nextRecord: IApplicationLivestockRecord = {
      id: undefined,
      quantity: lshToNumberOrZero(livestockForm.quantity),
      notes: livestockForm.notes.trim() || null,
      type: livestockForm.type.trim() || selectedLivestock.livestockType,
      productGrading,
      sellOfPeriod: serialisedSellOffPeriod,
      price: lshToNumberOrZero(livestockForm.price),
      livestock: selectedLivestock,
      application: applicationId ? {id: Number(applicationId)} : undefined,
    };

    setLivestockRecords((current) => [...current, nextRecord]);
    setLivestockForm(EMPTY_LIVESTOCK_FORM);
    setSellOffFrequency('not-selling');
    setSellOffMonth('');
    setCustomSellOffValues({});
  };

  const handleRemoveLivestockRecord = async (
    recordToRemove: IApplicationLivestockRecord,
  ) => {
    setLivestockError(null);

    if (!recordToRemove.id) {
      setLivestockRecords((current) =>
        current.filter((record) => record !== recordToRemove),
      );
      return;
    }

    try {
      await apiDeleteFdApplicationLivestock(Number(recordToRemove.id));
      setLivestockRecords((current) =>
        current.filter((record) => record.id !== recordToRemove.id),
      );
    } catch (error) {
      console.error(
        '[ApplicationProductionInfoStep] Failed to remove livestock record',
        error,
      );
      setLivestockError(
        "We couldn't remove this livestock record. Please try again.",
      );
    }
  };

  const handleSaveLivestockStep = async (): Promise<boolean> => {
    if (!applicationId || isSavingLivestock) return false;

    try {
      setLivestockError(null);
      setIsSavingLivestock(true);

      if (livestockRecords.length > 0) {
        const hasInvalidGrading = livestockRecords.some(
          (record) => !lshNormalizeProductGrading(record.productGrading),
        );
        if (hasInvalidGrading) {
          setLivestockError(
            'One or more livestock entries have invalid product grading. Use only A, B or C.',
          );
          return false;
        }

        const upsertPayload = livestockRecords.map((record) => {
          const livestockId = Number(record.livestock?.id);
          if (!Number.isFinite(livestockId)) {
            throw new Error('Invalid livestock linked to one or more records.');
          }

          return {
            ...(record.id ? {id: Number(record.id)} : {}),
            applicationId: Number(applicationId),
            livestockId,
            quantity: Number(record.quantity ?? 0),
            notes: String(record.notes || '').trim(),
            type: String(record.type || '').trim(),
            productGrading:
              lshNormalizeProductGrading(record.productGrading) || 'A',
            sellOfPeriod: String(record.sellOfPeriod || '').trim(),
            price: Number(record.price ?? 0),
          };
        });

        const savedRecords =
          await apiCreateOrUpdateMultipleFdApplicationLivestock(upsertPayload);
        const mappedSavedRecords = (
          Array.isArray(savedRecords) ? savedRecords : []
        ).map((record) =>
          mapLivestockRecord(
            record as ILivestockRecordSource,
            Number(applicationId),
          ),
        );
        setLivestockRecords(mappedSavedRecords);
      }

      return true;
    } catch (error) {
      console.error(
        '[ApplicationProductionInfoStep] Failed to save livestock records',
        error,
      );
      setLivestockError("We couldn't save livestock data. Please try again.");
      return false;
    } finally {
      setIsSavingLivestock(false);
    }
  };

  return {
    onceOffMonths,
    customMonths,
    livestockSelectOptions,
    livestockTypeOptions,
    handleLivestockFormChange,
    handleSellOffFrequencyChange,
    handleSellOffMonthChange,
    handleCustomSellOffValueChange,
    handleAddLivestockRecord,
    handleRemoveLivestockRecord,
    handleSaveLivestockStep,
  };
}
